import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { getGlncProducts } from "../lib/api";
import { useCart } from "../context/CartContext";

const TAGLINES = {
  "skyline-arachnid":
    "Above the noise. Below the neon. Web-slung silhouettes for rooftops only.",
  "adamant-hoodie":
    "Bones like the rest of us — except the ones that matter. Cut, close, keep moving.",
};

export default function NerdVault() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    window.scrollTo(0, 0);
    getGlncProducts()
      .then((p) => setProducts(p))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const quickAdd = (product) => {
    const first =
      (product.variants || []).find((v) => v.available) || product.variants?.[0];
    addItem(product, first?.size, first?.color);
    toast.success(`${product.name} added`, {
      description: `${first?.size || "OS"} · ${first?.color || "Default"}`,
    });
    setCartOpen(true);
  };

  return (
    <div className="grain relative bg-black min-h-screen">
      <Nav />
      <main className="pt-32 pb-24 px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[#FFDE00]" />
              <span className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FFDE00]">
                GLNC · Nerd Corner
              </span>
            </div>
            <h1
              data-testid="nerd-title"
              className="font-display text-5xl md:text-8xl font-extrabold tracking-tighter text-white leading-[0.85]"
            >
              THE NERD<br />VAULT<span className="text-[#FFDE00]">.</span>
            </h1>
          </div>
          <p className="font-mono-tech text-sm text-steel-light max-w-sm">
            First drops for people who read the footnotes. If you know, you KNOW.
          </p>
        </div>

        <div className="border-t-2 border-[#FFDE00]/60 mt-4 mb-12" />

        {/* Grid or coming-soon */}
        {loading ? (
          <p className="font-mono-tech text-xs uppercase tracking-widest text-steel">
            Loading vault…
          </p>
        ) : products.length === 0 ? (
          <ComingSoon />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((p, i) => (
              <motion.article
                key={p.handle}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.08 }}
                data-testid={`nerd-card-${p.handle}`}
                className="group"
              >
                <Link to={`/product/${p.handle}`} className="block">
                  <div className="relative aspect-[3/4] overflow-hidden border-2 border-[#FFDE00]/40 bg-steel-dark/40 hover:border-[#FFDE00] transition-colors duration-300">
                    <img
                      src={p.image}
                      alt={p.name}
                      className="w-full h-full object-cover saturate-[0.55] group-hover:scale-105 transition-transform duration-[900ms] ease-out"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="font-mono-tech text-[10px] uppercase tracking-widest text-black bg-[#FFDE00] px-2 py-1">
                        Drop {String(i + 1).padStart(3, "0")}
                      </span>
                    </div>
                    <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white/0 group-hover:bg-[#FFDE00] flex items-center justify-center transition-colors duration-300">
                      <ArrowUpRight className="w-5 h-5 text-white group-hover:text-black transition-colors duration-300" />
                    </div>
                  </div>
                </Link>

                <div className="mt-4">
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="font-display text-xl font-bold tracking-tight text-white uppercase">
                      {p.name}
                    </h3>
                    <span className="font-mono-tech text-sm text-[#FFDE00]">
                      ${Number(p.price).toFixed(0)}
                    </span>
                  </div>
                  <p className="font-mono-tech text-xs text-steel-light leading-relaxed mb-4 min-h-[2.5em]">
                    {TAGLINES[p.handle] || (p.description || "").slice(0, 100)}
                  </p>
                  <button
                    data-testid={`nerd-add-${p.handle}`}
                    onClick={() => quickAdd(p)}
                    className="group/btn relative w-full overflow-hidden bg-[#FFDE00] text-black border-2 border-black shadow-[3px_3px_0_0_#000] hover:shadow-[5px_5px_0_0_#000] hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all duration-200 py-3"
                  >
                    <span className="relative z-10 font-mono-tech text-[11px] uppercase tracking-widest flex items-center justify-center gap-2">
                      <ShoppingBag className="w-3.5 h-3.5" /> Quick Add
                    </span>
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

function ComingSoon() {
  return (
    <div
      data-testid="nerd-empty"
      className="border-2 border-[#FFDE00] p-10 md:p-16 max-w-2xl bg-[#0a0a0a] shadow-[8px_8px_0_0_#FFDE00]"
    >
      <p className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FFDE00] mb-4">
        Drop 001 · Locked
      </p>
      <h2 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter text-white leading-[0.85] mb-6">
        COMING<br />SOON<span className="text-[#FFDE00]">.</span>
      </h2>
      <p className="font-mono-tech text-sm text-steel-light leading-relaxed">
        The drip hasn't spawned yet. Come back after the timeline resets.
      </p>
    </div>
  );
}
